import React, { useMemo, useState } from "react";
import { FormGroup, Label, Input } from "@/design-system/native";
import { Gantt, ViewMode } from "gantt-task-react";
import "gantt-task-react/dist/index.css";
import dayjs from "dayjs";
import styles from "./GanttComponent.module.scss";

/* ===== Renk teması (ekrandaki palette) ===== */
const COLORS = {
  blocked: { bar: "#E74C3C", progress: "#C0392B" }, // kırmızı
  inProgress: { bar: "#6C63FF", progress: "#5146FF" }, // mor
  plan: { bar: "#F6C453", progress: "#E7B23B" }, // turuncu
  default: { bar: "#5B9BD5", progress: "#4A89C2" },
};

const statusKey = (t) =>
  t.status === "Blocked" || t.status === "Overdue"
    ? "blocked"
    : t.status === "In Progress"
    ? "inProgress"
    : t.status === "Plan"
    ? "plan"
    : "default";

/* ===== Yardımcılar ===== */
const toDate = (val) => {
  const m = dayjs(val, ["DD-MM-YYYY HH:mm", "DD-MM-YYYY"], true);
  return m.isValid() ? m.toDate() : null;
};
const stripeBg = (w) => ({
  /* dikey açık mavi şeritler */
  backgroundImage: `repeating-linear-gradient(90deg,#f5faff 0,#f5faff ${w}px,#ffffff ${w}px,#ffffff ${
    w * 2
  }px)`,
});

/* ===== Grafik ===== */
const GanttChart = ({ tasks, viewMode, columnWidth }) => {
  const data = useMemo(() => {
    return (tasks || [])
      .map((t) => {
        const start = toDate(t.startDate);
        const end = toDate(t.deadline);
        if (!start || !end) return null;

        const key = statusKey(t);
        const { bar, progress } = COLORS[key];

        return {
          id: String(t.id),
          name: t.title,
          start,
          end,
          type: t.milestone ? "milestone" : t.project ? "project" : "task",
          progress: typeof t.progress === "number" ? t.progress : 100,
          project: t.projectId || undefined,
          isDisabled: false,
          styles: {
            backgroundColor: bar,
            progressColor: progress,
            progressSelectedColor: progress,
            backgroundSelectedColor: bar,
          },
        };
      })
      .filter(Boolean);
  }, [tasks]);

  if (!data.length) {
    return (
      <div className={styles.empty}>
        Geçerli tarih aralığına sahip görev bulunamadı.
      </div>
    );
  }

  return (
    <div className={styles.chart} style={stripeBg(columnWidth)}>
      <Gantt
        tasks={data}
        viewMode={
          ViewMode[viewMode]
        } /* Week görünümü ay başlıklarını ve haftalık tık işaretlerini verir */
        listCellWidth="220px"
        columnWidth={
          columnWidth
        } /* zoom ile eşleşir (şeritler de aynı genişlikte) */
        rowHeight={28} /* ince barlar */
        barCornerRadius={2}
        barFill={70}
        todayColor="#EAF2FF" /* bugünün çizgisi */
        locale="en-GB"
        preStepsCount={1}
      />
    </div>
  );
};

/* ===== Sağ panel (opsiyonel) ===== */
const STATUS_BADGE = (status) =>
  status === "In Progress"
    ? styles.badgeInfo
    : status === "Blocked" || status === "Overdue"
    ? styles.badgeDanger
    : status === "Plan"
    ? styles.badgeWarning
    : styles.badgeSecondary;

const TaskPanel = ({ tasks }) => (
  <div className={styles.panel}>
    <div className={styles.panelBody}>
      <h6 className={styles.panelTitle}>Görev Bilgileri</h6>
      <ul className={styles.panelList}>
        {tasks.map((t) => (
          <li key={t.id} className={styles.panelItem}>
            <strong>{t.title}</strong>{" "}
            <span className={`${styles.badge} ${STATUS_BADGE(t.status)}`}>
              {t.status || "—"}
            </span>{" "}
            <span className={styles.panelMeta}>
              — {t.relatedTo || "-"} / {t.assignedTo || "-"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

/* ===== Ana bileşen ===== */
const GanttComponent = ({ tasks }) => {
  const [viewMode, setViewMode] = useState("Week"); // ekranındaki gibi uzun zaman çizgisi için "Week"
  const [assignedTo, setAssignedTo] = useState("All");
  const [columnWidth, setColumnWidth] = useState(44); // zoom (ekrandaki yoğunluk için 40–48 iyi)

  const uniqueUsers = useMemo(
    () => [
      "All",
      ...Array.from(
        new Set((tasks || []).map((t) => t.assignedTo).filter(Boolean))
      ),
    ],
    [tasks]
  );

  const filteredTasks = useMemo(
    () =>
      assignedTo === "All"
        ? tasks
        : tasks.filter((t) => t.assignedTo === assignedTo),
    [tasks, assignedTo]
  );

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarCol}>
          <FormGroup className={styles.formGroup}>
            <Label className={styles.fieldLabel}>View</Label>
            <Input
              type="select"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
            >
              <option value="Day">Day</option>
              <option value="Week">Week</option>
              <option value="Month">Month</option>
            </Input>
          </FormGroup>
        </div>

        <div className={styles.toolbarCol}>
          <FormGroup className={styles.formGroup}>
            <Label className={styles.fieldLabel}>Assigned to</Label>
            <Input
              type="select"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
            >
              {uniqueUsers.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </Input>
          </FormGroup>
        </div>

        {/* Zoom kaydırıcısı (timeline yoğunluğu) */}
        <div className={styles.zoomCol}>
          <div className={styles.zoomControl}>
            <span className={styles.fieldLabel}>Zoom</span>
            <Input
              type="range"
              min={28}
              max={72}
              step={2}
              value={columnWidth}
              onChange={(e) => setColumnWidth(Number(e.target.value))}
              className={styles.zoomRange}
            />
          </div>
        </div>
      </div>

      {/* Gantt */}
      <div className={styles.chartRow}>
        <div className={styles.chartCol}>
          <GanttChart
            tasks={filteredTasks}
            viewMode={viewMode}
            columnWidth={columnWidth}
          />
        </div>
      </div>

      {/* Legend (ekrandaki gibi alt kısım) */}
      <div className={styles.legendRow}>
        <div className={styles.legendWrap}>
          <div className={styles.legend}>
            <span className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ "--swatch-bg": COLORS.blocked.bar }}
              />
              blocked / overdue
            </span>
            <span className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ "--swatch-bg": COLORS.inProgress.bar }}
              />
              in progress
            </span>
            <span className={styles.legendItem}>
              <span
                className={styles.legendSwatch}
                style={{ "--swatch-bg": COLORS.plan.bar }}
              />
              plan
            </span>
          </div>
        </div>
      </div>

      {/* Opsiyonel alt panel */}
      <div className={styles.panelRow}>
        <div className={styles.panelCol}>
          <TaskPanel tasks={filteredTasks} />
        </div>
      </div>
    </div>
  );
};

export default GanttComponent;
